import styled from "styled-components";
import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import ChargeModal from './Modal/ChargeModal';
import AccountInfoModal from './Modal/AccountInfoModal';
import CancelChargeModal from './Modal/CancelChargeModal';
import TaxInvoiceModal from './Modal/TaxInvoiceModal';
import SuccessModal from './Modal/SuccessModal';
import instance from "src/API/MainAPI";
import { LabsWalletRechargePagesAPI, WalletRechargeDataResponse } from "../TestManagement/TestManagementApi";
import { Link } from "react-router-dom";
import { LabsWalletRequestRechargeAPI, LabsWalletCancelRechargeAPI, LabsWalletCheckRechargeCompleteAPI, LabsWalletRechargeDetailsAPI, LabsWalletCompanyDetailsAPI, LabsWalletTaxInvoiceAPI } from "./Apis/PlimoneyApis";
import OverviewAPI from "../Home/Data/OverviewAPI";
import RechargeCompleteModal from './Modal/RechargeCompleteModal';
import RechargeDetailModal from "./Modal/RechargeDetailModal";

const Main = styled.main`
  background-color: #f8f9fa;
  width: 100%;
  padding-left: 195px;
  min-width: 1280px;
  padding-bottom: 180px;
  overflow-y: auto;
  height: calc(100vh - 180px);
`;

const TopWrapper = styled.div`
  padding-left: 125px;
  border-bottom: 1px solid #ededed;
  height: 120px;
  display: flex;
  align-items: center;
`;

const TopContainer = styled.div`
  width: 95%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TopTitle = styled.span`
  color: #2282e9;
  font-size: 20px;
  font-weight: 700;
`;

const BtnContainer = styled.div`
  display: flex;
  gap: 20px;
`;

const TopBtn = styled.button`
  width: 160px;
  height: 55px;
  border-radius: 5px;
  background-color: white;
  border: 1px solid #ededed;
  color: #c0c0c0;
  font-weight: 600;
`;

const AccentTopBtn = styled(TopBtn)`
  border: 1px solid #2282e9;
  color: #2282e9;
`;

const ContentWrapper = styled.div`
  padding-left: 125px;
  padding-top: 40px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 20px;
`;

const MoneyContainer = styled.div`
  background-color: white;
  border-radius: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 40px;
  width: 35%;
  height: 100px;
  margin-bottom: 70px;
`;

// 플리머니 충전 대기 테이블 스타일
const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 0px 0;
  margin-bottom: 20px;

`;

const TableContainer = styled.div`
  width: 90%;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #f0f8ff;
  padding: 15px 0;
`;

const TableHeaderCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: #555;
  font-weight: 500;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: white;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
`;

const TableCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: #333;
`;

const ActionStatusButton = styled.button<{ color: string, status: string }>`
  background-color: transparent;
  color: ${props => props.color};
  border: none;
  cursor: ${props => props.status === '거래취소' ? 'default' : 'pointer'};
  
  text-decoration: ${props => (props.status === '거래취소' ||  props.status === '입금완료') ? 'none' : 'underline'};
  font-size: 14px;
`;

const ActionTaxInvoiceStatusButton = styled.button<{ color: string, status: string }>`
  background-color: transparent;
  color: ${props => props.status !== '입금완료' ? '#000000' : props.color};
  border: none;
  cursor: ${props => props.status !== '입금완료' ? 'default' : 'pointer'};
  
  text-decoration: ${props => ( props.status !== '입금완료') ? 'none' : 'underline'};
  font-size: 14px;
`;

const ActionButton = styled.button<{ color: string, status: string, isRequested?: boolean }>`
  background-color: transparent;
  color: ${props => props.isRequested ? '#000000' : props.color};
  border: none;
  cursor: pointer;
  text-decoration: ${props => props.isRequested ? 'none' : 'underline'};
  font-size: 14px;
`;

const HistoryTableHeader = styled.div`
  display: grid;
  grid-template-columns: 0.7fr 1.5fr 1fr 1fr 1fr 1fr;
  background-color: #f0f8ff;
  padding: 15px 0;
`;

const HistoryTableHeaderCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: #555;
  font-weight: 500;
`;

const HistoryTableRow = styled.div`
  display: grid;
  grid-template-columns: 0.7fr 1.5fr 1fr 1fr 1fr 1fr;
  background-color: white;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
`;

const HistoryTableCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: #333;
`;

const AmountText = styled.span<{ isPositive: boolean }>`
  color: ${props => props.isPositive ? '#2282e9' : '#ff6b6b'};
  font-weight: 500;
`;

// 플리머니 내역 타입 정의
interface Transaction {
  id: number;
  createdAt: string;
  status: string;
  amount: number;
  note: string;
  category: string;
}

interface TransactionPage {
  content: Transaction[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  }
}

interface TransactionResponse {
  count: number;
  page: TransactionPage;
}

// 내역 조회 API 호출 함수
const fetchTransactions = async (page: number, size: number): Promise<TransactionResponse> => {
  const response = await instance.get(`/v1/labs/wallets/transactions?pageNumber=${page}&pageSize=${size}`);
  return response.data;
};

// 로딩 중 표시를 위한 스타일 컴포넌트 추가
const LoadingRow = styled.div`
  display: grid;
  grid-template-columns: 0.7fr 1.5fr 1fr 1fr 1fr 1fr;
  background-color: white;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
`;

const LoadingCell = styled.div`
  grid-column: 1 / span 6;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: #333;
  padding: 20px 0;
`;

// 페이지네이션 스타일 컴포넌트 추가
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 40px;
  width: 90%;
`;

const PageButton = styled.button<{ isActive?: boolean }>`
  width: 40px;
  height: 40px;
  margin: 0 5px;
  border: ${props => props.isActive ? 'none' : '1px solid #e0e0e0'};
  border-radius: 5px;
  background-color: ${props => props.isActive ? '#2282e9' : 'white'};
  color: ${props => props.isActive ? 'white' : '#333'};
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.isActive ? '#2282e9' : '#f5f5f5'};
  }
`;

const PageNavButton = styled(PageButton)`
  // margin-right: 10;
  width: 40px;
  background-color: white;
  color: #666;
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// 환불문의 링크를 위한 통합 styled-component
const RefundInquiry = styled.div`
  position: relative;
  width: 100%;
  
  & > a {
    position: absolute;
    right: 10%;
    color: #9F9F9F;
    font-size: 14px;
    font-weight: 500;

  }
`;

// API 응답 타입 정의 추가
interface RechargeInfo {
  bankAccountInfo: {
    bankName: string;
    accountNumber: string;
    accountOwner: string;
  };
  amount: number;
  depositDeadline: string;
}

interface RechargeRequest {
  amount: number;
  depositorName: string;
}

interface CompanyInfo {
  companyName: string;
  email: string;
  phone: string;
}


export default function PliMoneyCharge() {
  // 모달 표시 상태
  const [showChargeModal, setShowChargeModal] = useState(false);
  // 충전 금액
  const [amount, setAmount] = useState<string>("");
  // 결제 수단
  const [paymentMethod, setPaymentMethod] = useState<string>("무통장 입금");
  // 입금자명
  const [depositorName, setDepositorName] = useState<string>("");
  // 약관 동의
  const [termsAgreed, setTermsAgreed] = useState<boolean>(false);
  // 계좌정보 모달 표시 상태
  const [showAccountInfoModal, setShowAccountInfoModal] = useState(false);

  // 플리머니 내역 페이지네이션 관련 상태
  const [historyCurrentPage, setHistoryCurrentPage] = useState<number>(0);
  const [historyPageSize, setHistoryPageSize] = useState<number>(10);

  // 플리머니 충전 대기 페이지네이션 관련 상태
  const [waitingCurrentPage, setWaitingCurrentPage] = useState<number>(0);
  const [waitingPageSize, setWaitingPageSize] = useState<number>(10);

  const { data: companyInfo } = useQuery<CompanyInfo>({
    queryKey: ['companyInfo'],
    queryFn: async () => {
      const response = await LabsWalletCompanyDetailsAPI();
      return response.data;
    },
    enabled: true
  });

  // 플리머니 충전 대기 내역 조회
  const { 
    data: rechargeData, 
    isLoading: isRechargeDataLoading,
    refetch: refetchRechargeData
  } = useQuery<WalletRechargeDataResponse>({
    queryKey: ['transactionData', waitingCurrentPage, waitingPageSize],
    queryFn: async () => {
      const response = await LabsWalletRechargePagesAPI(waitingCurrentPage, waitingPageSize);
      return response.data;
    },
    enabled: true
  });

  // 주기적 API 호출을 위한 interval ID 저장용 ref
  const intervalRef = useRef<number | null>(null);

  // 폴링 처리
  // 입금대기 상태 확인 및 주기적 API 호출 설정
  useEffect(() => {
    // 이전 interval 제거
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // 초기 입금대기 정보 확인
    const checkInitialPendingRecharge = async () => {
      try {
        const response = await LabsWalletRechargePagesAPI(waitingCurrentPage, 1);
        const currentPendingCharges = response.data.page.content;
        
        // 입금대기 상태인 충전 건 찾기
        const pendingRecharge = currentPendingCharges.find((charge: any) => charge.status === "입금대기");
        
        if (pendingRecharge) {
          // sessionStorage에 저장
          sessionStorage.setItem('pendingRechargeId', pendingRecharge.id.toString());
        } else {
          // 입금대기 상태가 없으면 sessionStorage에서 제거
          sessionStorage.removeItem('pendingRechargeId');
        }
      } catch (error) {
        console.error('입금 확인 초기화 오류:', error);
      }
    };

    // 초기 확인 실행
    checkInitialPendingRecharge();

    // 주기적으로 실행되는 interval 설정
    intervalRef.current = window.setInterval(async () => {
      try {
        // sessionStorage에서 저장된 pendingRechargeId 확인
        const pendingRechargeId = sessionStorage.getItem('pendingRechargeId');
        
        if (pendingRechargeId) {
          // 저장된 ID가 있으면 입금 완료 여부 확인
          const result = await LabsWalletCheckRechargeCompleteAPI(Number(pendingRechargeId));
          
          if (result?.data?.complete) {
            // 데이터 갱신
            await refetchRechargeData();
            // sessionStorage에서 제거
            sessionStorage.removeItem('pendingRechargeId');
            // 완료 모달 표시
            setShowRechargeCompleteModal(true);
          }
        } else {
          // 저장된 ID가 없으면 새로운 입금대기 건 확인
          const response = await LabsWalletRechargePagesAPI(waitingCurrentPage, 1);
          const currentPendingCharges = response.data.page.content;
          
          const pendingRecharge = currentPendingCharges.find((charge: any) => charge.status === "입금대기");
          
          if (pendingRecharge) {
            // 새로운 입금대기 건이 있으면 sessionStorage에 저장
            sessionStorage.setItem('pendingRechargeId', pendingRecharge.id.toString());
          }
        }
      } catch (error) {
        console.error('입금 확인 API 오류:', error);
      }
    }, 3000); // 2초 간격으로 실행

    // 컴포넌트 언마운트 시 cleanup
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);


  // // API 데이터 쿼리 - 내역 정보
  
  const {
    data: transactionData,
    isLoading: isTransactionsLoading,
    refetch: refetchTransactions
  } = useQuery<TransactionResponse>({
    queryKey: ['transactions', historyCurrentPage, historyPageSize],
    queryFn: () => fetchTransactions(historyCurrentPage, historyPageSize),
    enabled: true
  });

// 충전 내역 상태
const [pendingCharges, setPendingCharges] = useState<any[]>([]);
const [transactionHistory, setTransactionHistory] = useState<any[]>([]);

// 취소하기 모달 관련 상태 추가
const [showCancelModal, setShowCancelModal] = useState(false);
const [selectedCharge, setSelectedCharge] = useState<any>(null);
const [selectedChargeId, setSelectedChargeId] = useState<number | null>(null);
// 세금계산서 모달 관련 상태 추가
const [showTaxInvoiceModal, setShowTaxInvoiceModal] = useState(false);
const [selectedChargeForTax, setSelectedChargeForTax] = useState<any>(null);

// 성공 모달 관련 상태 추가
const [showSuccessModal, setShowSuccessModal] = useState(false);

// rechargeInfo 상태 추가
const [rechargeInfo, setRechargeInfo] = useState<RechargeInfo | null>(null);

// 지갑 정보(플리머니 잔액)를 위한 별도 쿼리 추가
const { 
  data: overviewData, 
  isLoading: isPlimoneysLoading
} = useQuery({
  queryKey: ['plimoney'],
  queryFn: async () => {
    const response = await OverviewAPI();
    return response.data;
  },
  enabled: true
});

// console.log('plimoney: ',plimoney);

// 현재 날짜 생성
const getCurrentDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 지갑 데이터가 로드되면 충전 대기 내역 설정
useEffect(() => {
  if (rechargeData && rechargeData.page && rechargeData.page.content) {
    setPendingCharges(rechargeData.page.content);
  } else {
    setPendingCharges([]);
  }
}, [rechargeData]);


// 에러 메시지를 표시하기 위한 상태 추가
const [errorMessage, setErrorMessage] = useState<string>("");

// useMutation 설정 업데이트
const rechargesMutation = useMutation<any, Error, RechargeRequest>({
  mutationFn: async (request: RechargeRequest) => {
    const response = await LabsWalletRequestRechargeAPI(request);
    return response?.data;
  },
  onSuccess: (data) => {
    // API 응답 데이터 저장
    setRechargeInfo(data);
    setErrorMessage(""); // 성공 시 에러 메시지 초기화
    // 첫 번째 모달을 숨기고 계좌정보 모달을 표시
    setShowChargeModal(false);
    setShowAccountInfoModal(true);
  },
  onError: (error) => {
    // 에러 처리
    setErrorMessage("충전 대기 이력이 존재합니다. 취소 후 재신청 해주세요.");
  }
});

// 충전 취소 API를 위한 useMutation 추가
const cancelRechargesMutation = useMutation({
  mutationFn: LabsWalletCancelRechargeAPI,
  onSuccess: () => {
    // 성공적으로 취소된 경우의 처리
    setPendingCharges(pendingCharges.filter(charge => charge.id !== selectedCharge.id));
    setShowCancelModal(false);
    setSelectedCharge(null);
    alert("충전이 취소되었습니다.");
  },
  onError: (error) => {
    // 에러 처리
    console.error('충전 취소 요청 실패:', error);
    alert('충전 취소 요청에 실패했습니다. 다시 시도해주세요.');
    setShowCancelModal(false);
  }
});

// 모달 열기
const openChargeModal = () => {
  setShowChargeModal(true);
};

// 모달 닫기
const closeModals = () => {
  setShowChargeModal(false);
  setShowAccountInfoModal(false);
  setAmount("");
  setErrorMessage("");
  setPaymentMethod("무통장 입금");
  setDepositorName("");
  setTermsAgreed(false);
};

// 금액 선택
const handleAmountSelect = (selectedAmount: number) => {
  const currentAmount = parseInt(amount || '0');
  setAmount((currentAmount + selectedAmount).toString());
};

// 직접 입력 핸들러
const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  // 숫자만 입력 가능하도록
  const value = e.target.value.replace(/[^0-9]/g, '');
  setAmount(value);
};

// 결제 수단 선택
const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setPaymentMethod(e.target.value);
};

// 입금자명 변경
const handleDepositorNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setDepositorName(e.target.value);
};

// 약관 동의 체크
const handleTermsAgreementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setTermsAgreed(e.target.checked);
};

// handleNext 함수 수정
const handleNext = () => {
  // 입력값 확인 및 API 호출
  if (!amount || !depositorName) {
    alert('금액과 입금자명을 모두 입력해주세요.');
    return;
  }
  
  // API 호출
  rechargesMutation.mutate({
    amount: parseInt(amount),
    depositorName: depositorName
  });
};

// 확인 버튼 클릭
const handleConfirm = () => {
  // 충전 내역 추가
  const newCharge = {
    id: pendingCharges.length + 1,
    date: getCurrentDate(),
    method: "입금대기",
    amount: parseInt(amount),
    status: "입금대기"
  };

  setPendingCharges([...pendingCharges, newCharge]);
  closeModals();
  window.location.reload();
};

// 금액 형식화 (1000 -> 1,000)
const formatAmount = (amount: string | number) => {
  if (!amount) return "0";
  return parseInt(amount.toString(), 10).toLocaleString();
};

// 취소하기 버튼 클릭 핸들러
const handleCancelClick = async (charge: any) => {
  if(charge.status!== '입금대기'){
    return;
  }

  const result = await LabsWalletRechargeDetailsAPI(charge.id);

  console.log("test");
  setSelectedCharge(result.data);
  setSelectedChargeId(charge.id);
  setShowCancelModal(true);
};

// 충전 취소 확인 핸들러
const handleConfirmCancel = () => {
  if (selectedCharge && selectedCharge.id) {
    // API 호출
    cancelRechargesMutation.mutate(selectedCharge.id);
  } else {
    alert("취소할 충전 내역을 찾을 수 없습니다.");
    setShowCancelModal(false);
    setSelectedCharge(null);
  }
};

// 취소 모달 닫기
const closeCancelModal = () => {
  setShowCancelModal(false);
  setSelectedCharge(null);
};

// 세금계산서 신청하기 버튼 클릭 핸들러
const handleTaxInvoiceClick = (charge: any) => {
  if(charge.status !== '입금완료'){
    return;
  }
  setSelectedChargeForTax(charge);
  setShowTaxInvoiceModal(true);
};

// 세금계산서 신청 확인 핸들러
const handleSubmitTaxInvoice = async (businessLicenseFile: File, businessCertFile: File) => {
  if (!selectedChargeForTax || !companyInfo) {
    return;
  }

  // API 호출 시 에러를 throw하여 모달에서 처리할 수 있도록 함
  await LabsWalletTaxInvoiceAPI(
    selectedChargeForTax.id,
    businessCertFile,
    businessLicenseFile,
    {
      companyName: companyInfo.companyName,
      email: companyInfo.email,
      phone: companyInfo.phone
    }
  );
  
  // API 호출이 성공한 경우에만 실행
  setShowTaxInvoiceModal(false);
  setSelectedChargeForTax(null);
  setShowSuccessModal(true);
};

// 세금계산서 모달 닫기
const closeTaxInvoiceModal = () => {
  setShowTaxInvoiceModal(false);
  setSelectedChargeForTax(null);
};

// 성공 모달 닫기
const closeSuccessModal = () => {
  setShowSuccessModal(false);
};

// 플리머니 내역 페이지 변경 핸들러
const handleHistoryPageChange = (newPage: number) => {
  setHistoryCurrentPage(newPage);
  setTimeout(() => {
    refetchTransactions();
  }, 100);
};

// 플리머니 내역 페이지 크기 변경 핸들러
const handleHistoryPageSizeChange = (newSize: number) => {
  setHistoryPageSize(newSize);
  setHistoryCurrentPage(0);
  setTimeout(() => {
    refetchTransactions();
  }, 100);
};

// 플리머니 충전 대기 페이지 변경 핸들러
const handleWaitingPageChange = (newPage: number) => {
  setWaitingCurrentPage(newPage);
  setTimeout(() => {
    refetchRechargeData();
  }, 100);
};

// 플리머니 충전 대기 페이지 크기 변경 핸들러
const handleWaitingPageSizeChange = (newSize: number) => {
  setWaitingPageSize(newSize);
  setWaitingCurrentPage(0);
  setTimeout(() => {
    refetchRechargeData();
  }, 100);
};

// 플리머니 충전 대기 페이지네이션 버튼 렌더링
const renderWaitingPaginationButtons = () => {
  if (!rechargeData || !rechargeData.page || !rechargeData.page.pageable) return null;

  const { totalPages, pageNumber } = rechargeData.page.pageable;
  const visiblePages = 5;

  let startPage = Math.max(0, pageNumber - Math.floor(visiblePages / 2));
  let endPage = Math.min(totalPages - 1, startPage + visiblePages - 1);

  if (endPage - startPage + 1 < visiblePages) {
    startPage = Math.max(0, endPage - visiblePages + 1);
  }

  const pages = [];

  // 이전 페이지 버튼
  pages.push(
    <PageNavButton
      key="prev"
      onClick={() => handleWaitingPageChange(pageNumber - 1)}
      disabled={pageNumber === 0}
    >
      &lt;
    </PageNavButton>
  );

  // 페이지 번호 버튼
  for (let i = startPage; i <= endPage; i++) {
    pages.push(
      <PageButton
        key={i}
        isActive={i === pageNumber}
        onClick={() => handleWaitingPageChange(i)}
      >
        {i + 1}
      </PageButton>
    );
  }

  // 다음 페이지 버튼
  pages.push(
    <PageNavButton
      key="next"
      onClick={() => handleWaitingPageChange(pageNumber + 1)}
      disabled={pageNumber === totalPages - 1}
    >
      &gt;
    </PageNavButton>
  );

  return pages;
};

// 입금 완료 모달 상태 추가
const [showRechargeCompleteModal, setShowRechargeCompleteModal] = useState(false);

// 입금 완료 모달 닫기 핸들러
const handleRechargeCompleteModalClose = () => {
  setShowRechargeCompleteModal(false);
};

// 플리머니 내역 페이지네이션 버튼 렌더링
const renderHistoryPaginationButtons = () => {
  if (!transactionData || !transactionData.page || !transactionData.page.pageable) return null;

  const { totalPages, pageNumber } = transactionData.page.pageable;
  const visiblePages = 5;

  let startPage = Math.max(0, pageNumber - Math.floor(visiblePages / 2));
  let endPage = Math.min(totalPages - 1, startPage + visiblePages - 1);

  if (endPage - startPage + 1 < visiblePages) {
    startPage = Math.max(0, endPage - visiblePages + 1);
  }

  const pages = [];

  // 이전 페이지 버튼
  pages.push(
    <PageNavButton
      key="prev"
      onClick={() => handleHistoryPageChange(pageNumber - 1)}
      disabled={pageNumber === 0}
    >
      &lt;
    </PageNavButton>
  );

  // 페이지 번호 버튼
  for (let i = startPage; i <= endPage; i++) {
    pages.push(
      <PageButton
        key={i}
        isActive={i === pageNumber}
        onClick={() => handleHistoryPageChange(i)}
      >
        {i + 1}
      </PageButton>
    );
  }

  // 다음 페이지 버튼
  pages.push(
    <PageNavButton
      key="next"
      onClick={() => handleHistoryPageChange(pageNumber + 1)}
      disabled={pageNumber === totalPages - 1}
    >
      &gt;
    </PageNavButton>
  );

  return pages;
};

// 상세보기 모달 상태 추가
const [showDetailModal, setShowDetailModal] = useState(false);
const [selectedDetail, setSelectedDetail] = useState<any>(null);

// 상세보기 클릭 핸들러 추가
const handleDetailClick = async (charge: any) => {
  try {
    const result = await LabsWalletRechargeDetailsAPI(charge.id);
    setSelectedDetail(result.data);
    setShowDetailModal(true);
  } catch (error) {
    console.error('상세 정보 조회 실패:', error);
    alert('상세 정보를 불러오는데 실패했습니다.');
  }
};

// 상세보기 모달 닫기 핸들러
const closeDetailModal = () => {
  setShowDetailModal(false);
  setSelectedDetail(null);
};

return (
  <Main>
    <TopWrapper>
      <TopContainer>
        <TopTitle>플리머니 충전</TopTitle>
        <BtnContainer>
          <AccentTopBtn onClick={openChargeModal}>충전하기</AccentTopBtn>
        </BtnContainer>
      </TopContainer>
    </TopWrapper>
    <ContentWrapper>
      <Title>플리머니 잔액</Title>
      <MoneyContainer>
        <span style={{ color: "#797979", fontWeight: "500" }}>플리머니</span>
        <div style={{ display: "flex", gap: "10px" }}>
          <span
            style={{ color: "#2282e9", fontWeight: "600", fontSize: "20px" }}
          >
            {isPlimoneysLoading ? 0 : formatAmount(overviewData?.plimoney || 0)}
          </span>
          <span
            style={{ fontWeight: "700", fontSize: "18px", color: "#555555" }}
          >
            머니
          </span>
        </div>
      </MoneyContainer>

      {/* 플리머니 충전 대기 섹션 */}
      { pendingCharges.length > 0 && (
        <>
          <SectionTitle>플리머니 충전 이력</SectionTitle>
          
          <TableContainer>
            <TableHeader>
              <TableHeaderCell>주문일시</TableHeaderCell>
              <TableHeaderCell>입금자명</TableHeaderCell>
              <TableHeaderCell>주문 금액</TableHeaderCell>
              <TableHeaderCell>거래상태</TableHeaderCell>
              <TableHeaderCell>입금정보</TableHeaderCell>
              <TableHeaderCell>세금계산서</TableHeaderCell>
              <TableHeaderCell>변경</TableHeaderCell>
            </TableHeader>
            {pendingCharges.map((charge) => (
              <TableRow key={charge.id}>
                <TableCell>{charge.createdAt}</TableCell>
                <TableCell>{charge.ordererName}</TableCell>
                <TableCell>{formatAmount(charge.orderAmount)}</TableCell>
                <TableCell>{charge.status}</TableCell>
                <TableCell>
                  <ActionButton 
                    color="#2282e9" 
                    status={charge.status}
                    onClick={() => handleDetailClick(charge)}
                  >
                    상세보기
                  </ActionButton>
                </TableCell>
                <TableCell>
                  <ActionTaxInvoiceStatusButton
                    color="#2282e9"
                    status={charge.status}
                    onClick={() => handleTaxInvoiceClick(charge)}
                    disabled={charge.taxInvoiceRequested}
                  >
                    {(charge.taxInvoiceRequested && charge.status === '입금완료') ? '신청완료' : (charge.status === '입금완료' ? '신청하기' : '---')}
                  </ActionTaxInvoiceStatusButton>
                </TableCell>
                <TableCell>
                  <ActionStatusButton
                     color={charge.status === '입금대기' ? "#ff6b6b" : "#000000"}
                    status={charge.status}
                    onClick={() => handleCancelClick(charge)}
                  >
                    {
                      (charge.status === '입금대기') ? '취소하기' : '---'
                    }
                  </ActionStatusButton>
                </TableCell>
              </TableRow>
            ))}
          </TableContainer>
          
          {/* 플리머니 충전 대기 섹션의 페이지네이션 */}
          {rechargeData?.page?.pageable && rechargeData.page.pageable.totalPages > 0 && (
            <PaginationContainer>
              {renderWaitingPaginationButtons()}
            </PaginationContainer>
          )}
          
          {/* 페이지 정보 표시 추가 */}
          {/* {rechargeData?.page?.pageable && (
            <div style={{ 
              width: '90%', 
              textAlign: 'center', 
              fontSize: '14px', 
              color: '#666', 
              marginTop: '-10px',
              marginBottom: '30px' 
            }}>
              전체 {rechargeData.page.pageable.totalElements}개 중 
              {' '}
              {waitingCurrentPage * waitingPageSize + 1}-
              {Math.min((waitingCurrentPage + 1) * waitingPageSize, rechargeData.page.pageable.totalElements)}개 표시
            </div>
          )} */}
        </>
      )}

      {/* 플리머니 내역 섹션 */}
      <SectionTitle>플리머니 내역</SectionTitle>
      
      {/* 페이지 크기 선택 옵션 추가 */}
      {/* <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', width: '90%' }}>
        <select 
          value={historyPageSize}
          onChange={(e) => handleHistoryPageSizeChange(Number(e.target.value))}
          style={{ 
            padding: '5px 10px', 
            borderRadius: '4px', 
            border: '1px solid #e0e0e0',
            background: 'white',
            fontSize: '14px'
          }}
        >
          <option value={5}>5개씩 보기</option>
          <option value={10}>10개씩 보기</option>
          <option value={20}>20개씩 보기</option>
          <option value={50}>50개씩 보기</option>
        </select>
      </div> */}
      
      <TableContainer>
        <HistoryTableHeader>
          <HistoryTableHeaderCell>번호</HistoryTableHeaderCell>
          <HistoryTableHeaderCell>거래일시</HistoryTableHeaderCell>
        
          <HistoryTableHeaderCell>거래상태</HistoryTableHeaderCell>
          <HistoryTableHeaderCell>금액</HistoryTableHeaderCell>
          <HistoryTableHeaderCell>비고</HistoryTableHeaderCell>
        </HistoryTableHeader>

        {!transactionData ? (
          <LoadingRow>
            <LoadingCell>로딩 중입니다...</LoadingCell>
          </LoadingRow>
        ) : transactionData.page.content.length > 0 ? (
          transactionData.page.content.map((transaction) => (
            <HistoryTableRow key={transaction.id}>
              <HistoryTableCell>{transaction.id}</HistoryTableCell>
              <HistoryTableCell>{transaction.createdAt}</HistoryTableCell>
            
              <HistoryTableCell>{transaction.status}</HistoryTableCell>
              <HistoryTableCell>
                <AmountText isPositive={transaction.category === "DEPOSIT"}>
                  {transaction.category === "DEPOSIT" ? '+ ' : '- '}
                  {formatAmount(transaction.amount)} 원
                </AmountText>
              </HistoryTableCell>
              <HistoryTableCell>{transaction.note || "--"}</HistoryTableCell>
            </HistoryTableRow>
          ))
        ) : (
          <LoadingRow>
            <LoadingCell>내역이 없습니다.</LoadingCell>
          </LoadingRow>
        )}
      </TableContainer>
      <RefundInquiry>
        <Link to="/home/inquiry">환불문의</Link>
      </RefundInquiry>

      {/* 플리머니 내역 페이지네이션 */}
      {transactionData?.page?.pageable && transactionData.page.pageable.totalPages > 0 && (
        <PaginationContainer>
          {renderHistoryPaginationButtons()}
        </PaginationContainer>
      )}
      
      {/* 페이지 정보 표시 추가 */}
      {/* {transactionData && (
        <div style={{ 
          width: '90%', 
          textAlign: 'center', 
          fontSize: '14px', 
          color: '#666', 
          marginTop: '-20px',
          marginBottom: '30px' 
        }}>
          전체 {transactionData.page.pageable.totalElements}개 중 
          {' '}
          {historyCurrentPage * historyPageSize + 1}-
          {Math.min((historyCurrentPage + 1) * historyPageSize, transactionData.page.pageable.totalElements)}개 표시
        </div>
      )} */}
    </ContentWrapper>

    {/* 충전 모달 */}
    {showChargeModal && (
      <ChargeModal
        onClose={closeModals}
        onNext={handleNext}
        amount={amount}
        paymentMethod={paymentMethod}
        depositorName={depositorName}
        termsAgreed={termsAgreed}
        onAmountChange={handleAmountChange}
        onAmountSelect={handleAmountSelect}
        onPaymentMethodChange={handlePaymentMethodChange}
        onDepositorNameChange={handleDepositorNameChange}
        onTermsAgreementChange={handleTermsAgreementChange}
        errorMessage={errorMessage}
      />
    )}

    {/* 계좌정보 모달 */}
    {showAccountInfoModal && (
      <AccountInfoModal
        onClose={closeModals}
        onConfirm={handleConfirm}
        amount={amount}
        rechargeInfo={rechargeInfo}
      />
    )}

    {/* 충전 취소 모달 */}
    {showCancelModal && selectedCharge && (
      <CancelChargeModal
        onClose={closeCancelModal}
        onCancel={handleConfirmCancel}
        chargeInfo={{
          rechargeId: Number(selectedChargeId),
          backName: selectedCharge?.bankAccountInfo?.bankName,
          accountNumber: selectedCharge?.bankAccountInfo?.accountNumber,
          accountHolder: selectedCharge?.bankAccountInfo?.accountOwner,
          amount: selectedCharge.amount,
          dueDate: selectedCharge?.depositDeadline
        }}
      />
    )}

    {/* 세금계산서 모달 */}
    {showTaxInvoiceModal && selectedChargeForTax && companyInfo && (
      <TaxInvoiceModal
        onClose={closeTaxInvoiceModal}
        onSubmit={handleSubmitTaxInvoice}
        businessInfo={{
          name: companyInfo.companyName,
          email: companyInfo.email,
          phone: companyInfo.phone
        }}
      />
    )}

    {/* 성공 모달 */}
    {showSuccessModal && (
      <SuccessModal onClose={closeSuccessModal} />
    )}

    {/* 입금 완료 모달 */}
    {showRechargeCompleteModal && (
      <RechargeCompleteModal onClose={handleRechargeCompleteModalClose} />
    )}

    {/* 상세보기 모달 */}
    {showDetailModal && selectedDetail && (
      <RechargeDetailModal
        onClose={closeDetailModal}
        onConfirm={closeDetailModal}
        amount=""
        rechargeInfo={selectedDetail}
      />
    )}
  </Main>
);
}
